import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { worldCupOverviewApi } from '../api';
import type {
  EventSettings,
  OverviewFocusNow,
  OverviewHealthRow,
  OverviewKpi,
  UpdateSwitchesPayload,
} from '../types';

type OverviewState = {
  kpi: OverviewKpi | null;
  focusNow: OverviewFocusNow | null;
  health: OverviewHealthRow[];
  switches: EventSettings;
};

const DEFAULT_SWITCHES: EventSettings = {
  maintenanceMode: false,
};

export type KpiRange = { from: string; to: string };

export const KPI_RANGE_DEFAULT: KpiRange = {
  from: '2026-06-11T00:00:00Z',
  to: '2026-07-19T23:59:59Z',
};

const EMPTY_RANGE: KpiRange = { ...KPI_RANGE_DEFAULT };

export function useWorldCupOverview() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const kpiAbortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<OverviewState>({
    kpi: null,
    focusNow: null,
    health: [],
    switches: DEFAULT_SWITCHES,
  });

  const [kpiRange, setKpiRange] = useState<KpiRange>(EMPTY_RANGE);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchKpi = useCallback(async (range: KpiRange, options?: { silent?: boolean }) => {
    kpiAbortRef.current?.abort();
    const controller = new AbortController();
    kpiAbortRef.current = controller;

    if (!options?.silent) setKpiLoading(true);

    try {
      const result = await worldCupOverviewApi.getKpi(
        { from: range.from || undefined, to: range.to || undefined },
        controller.signal
      );
      if (!mountedRef.current) return;
      setData((prev) => ({ ...prev, kpi: result }));
    } catch (err: any) {
      if (err?.name !== 'AbortError' && mountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err?.message || err)));
      }
    } finally {
      if (mountedRef.current) setKpiLoading(false);
    }
  }, []);

  const fetchOverview = useCallback(
    async (options?: { silent?: boolean }) => {
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      if (options?.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        const [kpiResult, focusNowResult, healthResult] = await Promise.allSettled([
          worldCupOverviewApi.getKpi(
            { from: kpiRange.from || undefined, to: kpiRange.to || undefined },
            controller.signal
          ),
          worldCupOverviewApi.getFocusNow(controller.signal),
          worldCupOverviewApi.getHealth(controller.signal),
        ]);

        if (!mountedRef.current) return;

        setData((prev) => ({
          ...prev,
          kpi: kpiResult.status === 'fulfilled' ? kpiResult.value : prev.kpi,
          focusNow: focusNowResult.status === 'fulfilled' ? focusNowResult.value : prev.focusNow,
          health: healthResult.status === 'fulfilled' ? healthResult.value : prev.health,
        }));

        const failed = [kpiResult, focusNowResult, healthResult].find(
          (item) => item.status === 'rejected'
        );

        if (failed && failed.status === 'rejected') {
          setError(
            failed.reason instanceof Error ? failed.reason : new Error(String(failed.reason))
          );
        }
      } catch (err: any) {
        if (!mountedRef.current) return;

        if (err?.name !== 'AbortError') {
          setError(err instanceof Error ? err : new Error(String(err?.message || err)));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [kpiRange.from, kpiRange.to]
  );

  const updateSwitches = useCallback(async (payload: UpdateSwitchesPayload) => {
    setSwitching(true);

    try {
      const nextSettings = await worldCupOverviewApi.updateSwitches(payload);

      if (mountedRef.current) {
        setData((prev) => ({
          ...prev,
          switches: {
            ...prev.switches,
            ...nextSettings,
          },
        }));
      }

      return nextSettings;
    } finally {
      if (mountedRef.current) {
        setSwitching(false);
      }
    }
  }, []);

  const exportReport = useCallback(async () => {
    setExporting(true);

    try {
      return await worldCupOverviewApi.exportReport();
    } finally {
      if (mountedRef.current) {
        setExporting(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      kpiAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchKpi(kpiRange, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiRange.from, kpiRange.to]);

  return useMemo(
    () => ({
      ...data,
      kpiRange,
      loading,
      refreshing,
      kpiLoading,
      switching,
      exporting,
      error,
      setKpiRange,
      refetch: () => fetchOverview({ silent: true }),
      updateSwitches,
      exportReport,
    }),
    [
      data,
      kpiRange,
      loading,
      refreshing,
      kpiLoading,
      switching,
      exporting,
      error,
      fetchOverview,
      updateSwitches,
      exportReport,
    ]
  );
}
